import type { Facet } from '../@types/ElasticSearchResponse';
import { numberSeparator } from '../helpers/formats';
import type QueryFilter from '../helpers/query-filter';
import { notEmpty } from './formats';
import type { CombinedFacet } from './reselectors';

interface Alias {
  id?: string;
  filters: { displayName: string }[];
}

export const fromQueryFilter = (
  queryFilter: QueryFilter,
  _facets: Facet[] | CombinedFacet[],
  onMiss: (_key?: string) => Alias | null = (_key?: string) => null,
) => {
  const facets = _facets as CombinedFacet[];
  let location: Alias | null = null;
  const list = queryFilter
    .getKeys()
    .map((key) => {
      const facet = facets.find((f) => f && f.id === key);
      let filters = queryFilter.get(key).map((value) => ({
        displayName: value,
      }));

      if (!facet) {
        return onMiss(key);
      }

      if (queryFilter.get(key).length === 0) {
        const range = queryFilter.getRange(key);
        if (!range) {
          return null;
        }

        let min: string | null = range.min?.toString() || facet.min?.toString() || '0';
        let max: string | null = range.max?.toString() || facet.max?.toString() || '0';

        if (facet.valueFormat) {
          min = numberSeparator(Number.parseInt(range.min || '0', 10));
          max = numberSeparator(Number.parseInt(range.max || '0', 10));
        }

        const displayName = `${facet.prefix || ''}${min}-${max}${facet.suffix || ''}`;
        filters = [{ displayName }];
      }

      return {
        id: facet.id,
        filters,
      };
    })
    .filter(notEmpty);

  if (queryFilter.hasSpatial()) {
    const spatial = queryFilter.getSpatial();
    const title = `${spatial?.name} +${spatial?.distance}km`;

    location = {
      id: 'location',
      filters: [{ displayName: title }],
    };
  }

  return [location].concat(list).filter(notEmpty);
};
